import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Chip,
  Popover,
  InputAdornment,
  Typography,
  Divider,
  Button,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import MicIcon from '@mui/icons-material/Mic';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import OrganisationDropdown from '../Shared/OrganisationDropdown/index.tsx';
import { FlowBuilderActions } from '../../Services/_actions/index.js';
import SearchableDropdown from '../FlowBuilder/Common/SearchableDropdown/SearchableDropdown.tsx';
import { useRecoilState } from 'recoil';
import { aiStateAtom } from '../../Services/_states/index.js';
import CampaignDropdown from '../Shared/CampaignDropdown/index.tsx';
import PhoneNumberInput from '../Shared/PhoneNumber/PhoneNumberInput.tsx';
import { Circle } from '@mui/icons-material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { EllipsisWithTooltip } from '../Shared/Tables/TableV1.tsx';
// const models = {
//   ChatGPT: ["GPT-3.5", "GPT-4", "GPT-4o"],
//   Gemini: ["Gemini 1.5 Flash", "Gemini 1.5 Pro"],
//   MdAi:["Md AI"]
// };

interface ChatInputBoxProps {
  onSubmit: (
    query: string,
    context: {
      selectedCampaign: Object | null;
      selectedOrganisation: Object | null;
      files?: File[];
    }
  ) => void;
  loading?: boolean;
}

export default function ChatInputBox({ onSubmit, loading = false }: ChatInputBoxProps) {
  const [query, setQuery] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [chatInputState, setChatInputState] = useRecoilState(aiStateAtom);
  const [formData, setFormData] = useState<any>({ ...chatInputState });

  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileClick = () => {
    fileInputRef.current?.click();
  };

  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const [isListening, setIsListening] = useState(false);

 useEffect(() => {
  const phoneNumber = localStorage.getItem('phoneNumber') || '+91';
  const codeMatch = phoneNumber.match(/^\+\d+/);
  const number = phoneNumber.replace(codeMatch?.[0] || '', '');
  setFormData((prev) => ({
    ...prev,
    phone: {
      code: codeMatch?.[0] || '+91',
      number: number || '',
    },
  }));
}, []);

  const handleMicClick = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      alert('Speech recognition not supported in this browser.');
      return;
    }

    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognitionRef.current = recognition;
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let interimTranscript = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          setQuery((prev) => prev + result[0].transcript + ' ');
        } else {
          interimTranscript += result[0].transcript;
        }
      }
      // Optional: show live interim transcript somewhere if needed
      // console.log('Interim:', interimTranscript);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognition.onerror = (e) => {
      console.error(e);
      setIsListening(false);
    };

    recognition.start();
    setIsListening(true);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    if (files.length > 0) {
      setUploadedFiles((prev) => [...prev, ...files]);
      event.target.value = '';
    }
  };
  const handleClear = (field: 'organization' | 'campaign') => {
    if (field === 'organization') {
      // setFormData({
      //   ...formData,
      //   selectedOrganisation: null,
      //   selectedCampaign: null,
      // });
      setChatInputState((prev) => ({
        ...prev,
        selectedOrganisation: null,
        selectedCampaign: null,
      }));
    } else if (field === 'campaign') {
      // setFormData({
      //   ...formData,
      //   selectedCampaign: null,
      // });

      setChatInputState((prev) => ({
        ...prev,
        selectedCampaign: null,
      }));
    }
  };

  const onSelectOrganisation = async (value) => {
    setFormData((prev) => ({
      ...prev,
      selectedOrganisation: value,
      selectedCampaign: null,
    }));
  };

  const open = Boolean(anchorEl);
  const hasContext = formData?.selectedOrganisation || formData?.selectedCampaign;

  const handleOpenContext = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseContext = () => {
    setAnchorEl(null);
    setChatInputState((prev) => ({ ...prev, ...formData }));
  };

  const handleSend = () => {
    if (!query.trim() && uploadedFiles.length === 0) return;
    if (isListening && recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
    }
    onSubmit(
      query.trim(),
      chatInputState
      // {
      //   organisation_id: chatInputState?.selectedOrganisation?.organisation_id,
      //   campaign_id: chatInputState?.selectedCampaign?.campaign_id,
      //   user_id: chatInputState?.user_id || '',
      //   files: uploadedFiles,
      // },
    );

    // Update the state if necessary
    // setChatInputState((prevState) => ({
    //   ...prevState,
    //   selectedOrganisation: chatInputState.selectedOrganisation,
    //   selectedCampaign: chatInputState.selectedCampaign,
    //   user_id: chatInputState.user_id,
    // }));
    setQuery('');
    setUploadedFiles([]);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        // bgcolor: '#f7f9fc',
        // border: '1px solid #ccc',
        backgroundColor: '#f8fcfffa',
        border: '1px solid #cde2ff',
        borderRadius: 2,
        px: { xs: 1, sm: 2 },
        pb: '5px',
        pt: '5px',
        width: '100%',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1, width: { xs: '100%', sm: '100%' } }}
        >
          <Button
            onClick={(e) => handleOpenContext(e)}
            size="small"
            sx={{
              fontSize: '12px',
              textTransform: 'none',
              p: { xs: '4px', sm: 1 },
              border: '1px solid #4962e5',
              lineHeight: 1.2,
              borderRadius: ' 15px !important',
              minWidth: { xs: 'auto', sm: 'fit-content' },
            }}
          >
            <AddCircleOutlineIcon
              fontSize="small"
              sx={{
                fontSize: { xs: '16px', sm: '14px' },
                color: 'primary.main',
                mr: { xs: 0, sm: 0.5 },
              }}
            />
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              Add Context
            </Box>
          </Button>
          <Box
            sx={{
              display: 'flex',
              gap: '5px',
              width: { xs: '100vw', sm: '100%' },
              padding: { xs: '5px', sm: '0px' },
              overflowX: { xs: 'scroll', sm: 'unset' },
              alignItems: 'center',
              color: 'primary.main',
            }}
          >
            {chatInputState.selectedOrganisation && (
              <Chip
                // label={
                //   (chatInputState.selectedOrganisation?.name || 'Organisation').length > 30
                //     ? `${chatInputState.selectedOrganisation.name.slice(0, 30)}...`
                //     : chatInputState.selectedOrganisation?.name || 'Organisation'
                // }
                label={
                  <EllipsisWithTooltip
                    text={chatInputState.selectedOrganisation?.name || 'Organisation'}
                    maxWidth={95} // You can adjust width to control truncation
                    fontSize={13}
                    fontWeight={500}
                    tooltipPlacement="top"
                  />
                }
                onDelete={() => handleClear('organization')}
                size="small"
                sx={{
                  // bgcolor: '#e3f2fd',
                  bgcolor: '#fff',
                  border: '1px solid #bfd8db',
                  maxWidth: '100%',
                  fontSize: '12px',
                  borderRadius: '3px',
                  color: '#5C5C5C',
                }}
              />
            )}
            {chatInputState.selectedCampaign && (
              <Chip
                label={
                  (chatInputState.selectedCampaign?.name || 'Campaign').length > 30
                    ? `${chatInputState.selectedCampaign.name.slice(0, 30)}...`
                    : chatInputState.selectedCampaign?.name || 'Campaign'
                }
                onDelete={() => handleClear('campaign')}
                size="small"
                sx={{
                  // bgcolor: '#e3f2fd',
                  bgcolor: '#fff',
                  border: '1px solid #bfd8db',
                  maxWidth: '100%',
                  fontSize: '12px',
                  borderRadius: '3px',
                  color: '#5C5C5C',
                }}
              />
            )}
          </Box>
        </Box>
      </Box>
      <Divider sx={{ my: '4px', borderColor: '#0d6efd' }} />
      <TextField
        fullWidth
        multiline
        maxRows={2}
        minRows={hasContext ? 1 : 1}
        sx={{ color: '#333333' }}
        placeholder="Type your query..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        disabled={loading}
        InputProps={{
          sx: {
            bgcolor: '#ffffff00',
            border: 'none',
            borderRadius: 2,
            paddingY: 0.5,
            paddingX: { xs: 0.5 },
            color: '#333333',
            '& .MuiInputBase-input::placeholder': {
              color: '#333333',
              opacity: 1,
            },
            '& fieldset': {
              border: 'none',
            },
          },
          startAdornment: (
            <InputAdornment position="start">
              <IconButton
                size="small"
                onClick={handleMicClick}
                color={isListening ? 'primary' : 'default'}
              >
                <MicIcon fontSize="small" sx={{ color: '#333333' }} />
              </IconButton>
              <IconButton size="small" onClick={handleFileClick}>
                <AttachFileIcon fontSize="small" sx={{ color: '#333333' }} />
              </IconButton>
              <input
                type="file"
                multiple
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={handleSend}
                disabled={(query.trim() === '' && uploadedFiles.length === 0) || loading}
                color="primary"
              >
                <SendIcon sx={{ color: '#333333' }} />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleCloseContext}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { p: 2, minWidth: 280 } }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          Add Context
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <OrganisationDropdown
          label={
            <Typography component="span">
              Organization <span style={{ color: 'red' }}>*</span>
            </Typography>
          }
          value={formData.selectedOrganisation || null}
          onChange={(value) => onSelectOrganisation(value)}
          autoSelectValue={false}
          sx={{ mb: 1, mt: 2 }}
        />

        {formData.selectedOrganisation?.organisation_id && (
          <CampaignDropdown
            label="Business Service"
            value={formData?.selectedCampaign || null}
            onChange={(value) => {
              setChatInputState((prev) => ({
                ...prev,
                selectedCampaign: value,
              }));
            }}
            organisationId={formData.selectedOrganisation?.organisation_id}
            sx={{ mb: 1 }}
            error={false}
            errorMessage=""
            disabled={false}
          />
        )}

        {formData?.selectedOrganisation?.organisation_id && (
          <PhoneNumberInput
            value={{
              code: formData?.phone?.code ?? '+91',
              number: formData?.phone?.number ?? '',
            }}
            onChange={(val) => {
              setFormData((prev) => ({
                ...prev,
                phone: val,
                user_id: val?.code?.replace('+', '') + val?.number,
              }));
            }}
          />
        )}
        {formData.selectedOrganisation?.organisation_id && (
          <TextField
            label="User Id"
            fullWidth
            value={formData?.user_id || ''}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                user_id: e.target.value,
              }))
            }
            sx={{ mb: 1, mt: 1 }}
            margin="dense"
            variant="outlined"
            size="small"
          />
        )}
        {formData?.selectedOrganisation?.organisation_id && (
          <TextField
            label="Initial Query Id"
            fullWidth
            value={formData?.initial_query_id || ''}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                initial_query_id: e.target.value,
              }))
            }
            sx={{ marginBottom: 1 }}
            margin="dense"
            variant="outlined"
            size="small"
          />
        )}
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={() => {
            handleCloseContext();
          }}
        >
          Submit
        </Button>
      </Popover>
    </Box>
  );
}
